import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, alpha } from '@mui/material'
import ImageIcon from '@mui/icons-material/Image'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../../generic/systemValues/systemValueLabel'
import { DEVICE_TYPE_ICONS } from '../utils/constants'

/**
 * Device visual component
 * Displays a representative image or icon of the device type
 */
const DeviceVisual = memo(({ deviceType, theme, colors }) => {
  const { t_ } = useSmartTranslation()
  const iconSrc = DEVICE_TYPE_ICONS[deviceType]

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        background: alpha(theme.palette.common.black, 0.4),
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${alpha(colors.border, 0.3)}`,
        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.2)'
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          opacity: 0.7,
          transition: 'opacity 0.3s',
          '&:hover': { opacity: 1 }
        }}
      >
        {iconSrc
          ? (
            <Box
              component='img'
              src={iconSrc}
              alt={deviceType}
              sx={{
                width: 64,
                height: 64,
                objectFit: 'contain',
                opacity: 0.8
              }}
            />
            )
          : (
            <ImageIcon sx={{ fontSize: 48, color: theme.palette.common.white, opacity: 0.5 }} />
            )}

        <Typography
          variant='caption'
          align='center'
          sx={{
            color: alpha(theme.palette.common.white, 0.7),
            fontWeight: 500,
            letterSpacing: 0.5
          }}
        >
          {deviceType ? systemValueLabel(t_, 'DeviceTypeEnum', deviceType) : t_('Device View')}
        </Typography>
      </Box>
    </Box>
  )
})

DeviceVisual.displayName = 'DeviceVisual'

DeviceVisual.propTypes = {
  deviceType: PropTypes.string,
  theme: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired
}

export default DeviceVisual
